import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { 
  Wifi, WifiOff, Download, RefreshCw, Database, Clock,
  CheckCircle, AlertTriangle, Trash2, HardDrive
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const CACHE_NAME = "tft-weather-offline-v1";
const CACHE_URL = "/offline/weather-cache.json";
const CACHE_EXPIRY = 60 * 60 * 1000; // 1 hour

const readOfflineCache = async () => {
  if (!window.caches) return null;
  const cache = await window.caches.open(CACHE_NAME);
  const response = await cache.match(CACHE_URL);
  if (!response) return null;
  return response.json();
};

const writeOfflineCache = async (data) => {
  if (!window.caches) throw new Error("Cache API unavailable");
  const cache = await window.caches.open(CACHE_NAME);
  await cache.put(
    CACHE_URL,
    new Response(JSON.stringify(data), { headers: { "Content-Type": "application/json" } })
  );
};

const deleteOfflineCache = async () => {
  if (!window.caches) return;
  const cache = await window.caches.open(CACHE_NAME);
  await cache.delete(CACHE_URL);
};

export default function OfflineMode({ 
  location, 
  forecast, 
  currentWeather,
  weather,
  extendedForecast,
  isDarkMode,
  onLoadCached 
}) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [cachedData, setCachedData] = useState(null);
  const [cacheSize, setCacheSize] = useState(0);
  const [lastSynced, setLastSynced] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);

  const loadCachedData = useCallback(() => {
    return (async () => {
      try {
        const data = await readOfflineCache();
        if (data) {
        const age = Date.now() - data.timestamp;
        
        if (age < CACHE_EXPIRY) {
          if (onLoadCached) {
            onLoadCached(data);
          }
          toast.info("Loaded cached weather data");
          return true;
        } else {
          toast.warning("Cached data is outdated");
        }
        }
      } catch (error) {
        console.error("Failed to load cached data:", error);
      }
      return false;
    })();
  }, [onLoadCached]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Listen for online/offline events
    const handleOnline = () => {
      setIsOnline(true);
      toast.success("You're back online!");
    };
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning("You're offline. Showing cached data.");
      void loadCachedData();
    };

    // Listen for service worker messages
    const handleMessage = (event) => {
      if (event.data.type === 'SYNC_WEATHER') {
        // Service worker requested a sync
        void saveToCache();
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    navigator.serviceWorker?.addEventListener("message", handleMessage);

    // Load cached data info
    void loadCachedInfo();

    // Auto-load cached data if offline on mount
    if (!navigator.onLine) {
      void loadCachedData();
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      navigator.serviceWorker?.removeEventListener("message", handleMessage);
    };
  }, [loadCachedData]);

  // Auto-sync when weather data changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (autoSyncEnabled && isOnline && weather && forecast && location) {
      const debounceTimer = setTimeout(() => {
        void saveToCache(true); // silent save
      }, 5000); // Save 5 seconds after data loads
      return () => clearTimeout(debounceTimer);
    }
  }, [weather, forecast, location, autoSyncEnabled, isOnline]);

  const loadCachedInfo = async () => {
    try {
      const data = await readOfflineCache();
      if (data) {
        setCachedData(data);
        setLastSynced(new Date(data.timestamp));
        
        // Calculate cache size
        const size = new Blob([JSON.stringify(data)]).size;
        setCacheSize(size);
      }
    } catch (error) {
      console.error("Failed to load cache info:", error);
    }
  };

  const saveToCache = async (silent = false) => {
    if (!silent) setSyncing(true);
    try {
      const cacheData = {
        timestamp: Date.now(),
        location,
        forecast,
        weather,
        currentWeather: currentWeather || weather,
        extendedForecast,
        savedAt: new Date().toISOString()
      };
      
      await writeOfflineCache(cacheData);
      setCachedData(cacheData);
      setLastSynced(new Date());
      
      const size = new Blob([JSON.stringify(cacheData)]).size;
      setCacheSize(size);
      
      // Also notify service worker to cache API responses
      if (navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_WEATHER',
          url: '/api/weather/current',
          data: weather
        });
      }
      
      if (!silent) {
        toast.success("Weather data saved for offline use!");
      }
    } catch (error) {
      if (!silent) {
        toast.error("Failed to save cache");
      }
    } finally {
      if (!silent) setSyncing(false);
    }
  };

  const clearCache = async () => {
    try {
      await deleteOfflineCache();
      setCachedData(null);
      setCacheSize(0);
      setLastSynced(null);
      
      // Also clear service worker cache
      if (navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLEAR_WEATHER_CACHE'
        });
      }
      
      toast.success("Cache cleared");
    } catch (error) {
      toast.error("Failed to clear cache");
    }
  };

  const toggleAutoSync = () => {
    setAutoSyncEnabled(!autoSyncEnabled);
    toast.info(autoSyncEnabled ? "Auto-sync disabled" : "Auto-sync enabled");
  };

  const formatCacheSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatTimeAgo = (date) => {
    if (!date) return "Never";
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return "Just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    return date.toLocaleDateString();
  };

  const isCacheValid = cachedData && (Date.now() - cachedData.timestamp) < CACHE_EXPIRY;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${
            isOnline 
              ? isDarkMode ? 'bg-green-500/20' : 'bg-green-100'
              : isDarkMode ? 'bg-red-500/20' : 'bg-red-100'
          }`}>
            {isOnline ? (
              <Wifi className="w-5 h-5 text-green-400" />
            ) : (
              <WifiOff className="w-5 h-5 text-red-400" />
            )}
          </div>
          <div>
            <h3 className="font-bold">Offline Mode</h3>
            <p className="text-xs opacity-60">
              {isOnline ? "Connected" : "Offline - Using cached data"}
            </p>
          </div>
        </div>
        
        <div className={`px-3 py-1 rounded-full text-xs font-medium ${
          isOnline 
            ? 'bg-green-500/20 text-green-400'
            : 'bg-red-500/20 text-red-400'
        }`}>
          {isOnline ? "Online" : "Offline"}
        </div>
      </div>

      {/* Connection Status */}
      <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <Database className={`w-6 h-6 mx-auto mb-2 ${
              cachedData ? 'text-green-400' : 'opacity-40'
            }`} />
            <p className="text-xs opacity-60">Cache Status</p>
            <p className={`text-sm font-medium ${
              isCacheValid ? 'text-green-400' : cachedData ? 'text-yellow-400' : 'text-red-400'
            }`}>
              {isCacheValid ? "Valid" : cachedData ? "Expired" : "Empty"}
            </p>
          </div>
          
          <div className="text-center">
            <HardDrive className="w-6 h-6 mx-auto mb-2 opacity-60" />
            <p className="text-xs opacity-60">Cache Size</p>
            <p className="text-sm font-medium">{formatCacheSize(cacheSize)}</p>
          </div>
        </div>
      </div>

      {/* Auto-Sync Toggle */}
      <div className={`p-3 rounded-xl flex items-center justify-between ${
        isDarkMode ? 'bg-white/5' : 'bg-black/5'
      }`}>
        <div className="flex items-center gap-2">
          <RefreshCw className={`w-4 h-4 ${autoSyncEnabled ? 'text-green-400' : 'opacity-40'}`} />
          <span className="text-sm opacity-70">Auto-sync when online</span>
        </div>
        <button
          onClick={toggleAutoSync}
          className={`w-10 h-5 rounded-full transition-colors relative ${
            autoSyncEnabled ? 'bg-green-500' : 'bg-white/20'
          }`}
        >
          <div className={`absolute w-4 h-4 bg-white rounded-full top-0.5 transition-transform ${
            autoSyncEnabled ? 'translate-x-5' : 'translate-x-0.5'
          }`} />
        </button>
      </div>

      {/* Last Synced */}
      <div className={`p-3 rounded-xl flex items-center justify-between ${
        isDarkMode ? 'bg-white/5' : 'bg-black/5'
      }`}>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 opacity-60" />
          <span className="text-sm opacity-70">Last synced</span>
        </div>
        <span className="text-sm font-medium">{formatTimeAgo(lastSynced)}</span>
      </div>

      {/* Cached Location */}
      {cachedData?.location && (
        <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <p className="text-xs opacity-60 mb-1">Cached Location</p>
          <p className="text-sm font-medium">{cachedData.location.name || "Unknown"}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          onClick={() => saveToCache(false)}
          disabled={syncing || !isOnline || !weather}
          className="flex-1 bg-[#00f2ea] text-black hover:bg-[#00d4d4]"
        >
          {syncing ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          {syncing ? "Saving..." : "Save for Offline"}
        </Button>
        
        {cachedData && (
          <Button
            variant="ghost"
            onClick={clearCache}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Tips */}
      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
        <p className="text-xs text-blue-400">
          <strong>Tip:</strong> Save weather data before going offline. Cached data is valid for 1 hour.
        </p>
      </div>

      {/* Offline Warning */}
      {!isOnline && !isCacheValid && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-xl ${isDarkMode ? 'bg-red-500/10' : 'bg-red-50'}`}
        >
          <div className="flex items-center gap-2 text-red-400 mb-2">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">No Valid Cache</span>
          </div>
          <p className="text-sm opacity-70">
            You&apos;re offline and no valid cached data is available. 
            Connect to the internet to fetch fresh weather data.
          </p>
        </motion.div>
      )}

      {/* Service Worker Status */}
      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
        <div className="flex items-center justify-between">
          <span className="text-sm opacity-70">App Cache</span>
          <div className="flex items-center gap-1">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span className="text-xs text-green-400">Enabled</span>
          </div>
        </div>
      </div>
    </div>
  );
}
